import type { Food, Meal } from './types';

/** A dish from the kitchen's list, as a meal rule remembers it. */
export interface MealRuleDish {
  dishId?: string;
  dishName: string;
  food: Food;
  category?: string;
  calories?: number | null;
  protein?: number | null;
}

/** One kind of tiffin in a meal, `quantity` of them: the day's special in one diet, or chosen dishes. */
export interface MealPortionPlan {
  id?: string;
  food: Food;
  mode?: 'special' | 'dishes';
  label?: string;
  dishes?: MealRuleDish[];
  /** Earlier saves: a single dish. */
  dish?: MealRuleDish;
  quantity?: number;
}

/** Earlier saves (combos): one line per kind of tiffin. */
export interface MealConfigItem {
  id?: string;
  kind?: 'standard' | 'dish';
  dishId?: string;
  name: string;
  food: Food;
  quantity: number;
  category?: string;
  calories?: number | null;
  protein?: number | null;
}

/** What a customer gets for one meal. New saves use `portions`; the other fields are earlier shapes, still read. */
export interface SlotMealRule {
  mode?: 'standard' | 'fixed' | 'combo' | 'portions';
  portions?: MealPortionPlan[];
  items?: MealConfigItem[];
  dish?: MealRuleDish;
  dishes?: MealRuleDish[];
}

export type SubscriptionMealRules = Partial<Record<Meal, SlotMealRule>>;

/** One kind of tiffin, however the rule was saved. No dishes means the day's special. */
export interface Tiffin {
  id: string;
  food: Food;
  dishes: MealRuleDish[];
  quantity: number;
}

const FOOD_WORD: Record<Food, string> = { veg: 'Veg', nonveg: 'Non-veg', egg: 'Egg' };

const count = (n: number | undefined) => Math.max(1, Math.floor(n ?? 1) || 1);

/** The tiffins a meal rule asks for, whatever shape it was saved in. No rule: one special in the customer's diet. */
export function tiffinsOf(rule: SlotMealRule | null | undefined, food: Food): Tiffin[] {
  if (rule?.portions?.length) {
    return rule.portions.map((p, i) => {
      const dishes = p.dishes?.length ? p.dishes : p.dish ? [p.dish] : [];
      return { id: p.id || `p-${i + 1}`, food: p.food, dishes: p.mode === 'special' ? [] : dishes, quantity: count(p.quantity) };
    });
  }
  if (rule?.items?.length) {
    return rule.items.map((it, i) => ({
      id: it.id || `p-${i + 1}`,
      food: it.food,
      dishes: it.kind === 'dish' || it.dishId
        ? [{ dishId: it.dishId, dishName: it.name, food: it.food, category: it.category, calories: it.calories, protein: it.protein }]
        : [],
      quantity: count(it.quantity),
    }));
  }
  const fixed = rule?.mode === 'fixed' ? (rule.dishes?.length ? rule.dishes : rule.dish ? [rule.dish] : []) : [];
  if (fixed.length) return [{ id: 'p-1', food: fixed[0]!.food, dishes: fixed, quantity: 1 }];
  return [{ id: 'p-1', food, dishes: [], quantity: 1 }];
}

/** "Paneer Bhurji / Dal Tadka" for a choice of dishes, "Veg special" for the day's special. */
export function tiffinName(t: Pick<Tiffin, 'food' | 'dishes'>): string {
  return t.dishes.length ? t.dishes.map((d) => d.dishName).join(' / ') : `${FOOD_WORD[t.food]} special`;
}

/** One kind of tiffin, and how many: identical tiffins count together. */
export interface TiffinLine {
  /** "Paneer Bhurji / Dal Tadka" for chosen dishes, "Veg special" for the day's special. */
  name: string;
  food: Food;
  quantity: number;
  /** Chosen dishes rather than the day's special. */
  custom: boolean;
  /** Several dishes, one of them each day. */
  pool: boolean;
}

/** The tiffins as lines, identical ones together; `times` for a meal that goes out more than once. */
export function tiffinLines(tiffins: readonly Tiffin[], times = 1): TiffinLine[] {
  const lines: TiffinLine[] = [];
  for (const t of tiffins) {
    const name = tiffinName(t);
    const same = lines.find((l) => l.name === name && l.food === t.food);
    if (same) same.quantity += t.quantity * times;
    else lines.push({ name, food: t.food, quantity: t.quantity * times, custom: t.dishes.length > 0, pool: t.dishes.length > 1 });
  }
  return lines;
}

/** "2× Paneer Bhurji + Egg special". */
export function tiffinsText(lines: readonly TiffinLine[]): string {
  return lines.map((l) => (l.quantity > 1 ? `${l.quantity}× ${l.name}` : l.name)).join(' + ');
}

/** For a row that already shows the diet and a count: one plain special needs no words, one dish no count. */
export function tiffinsLabel(lines: readonly TiffinLine[]): string {
  if (lines.length !== 1) return tiffinsText(lines);
  return lines[0]!.custom ? lines[0]!.name : '';
}

/** Nothing worth saving: one special in the customer's own diet, which is what having no rule means. */
export function isPlainMeal(tiffins: readonly Tiffin[], food: Food): boolean {
  const t = tiffins[0];
  return tiffins.length === 1 && !!t && t.dishes.length === 0 && t.food === food && t.quantity === 1;
}

/** The rules worth saving for a plan's meals, in the one shape new saves use; undefined when every meal is plain. */
export function mealRulesToSave(meals: readonly Meal[], rules: SubscriptionMealRules, food: Food): SubscriptionMealRules | undefined {
  const out: SubscriptionMealRules = {};
  for (const m of meals) {
    const tiffins = tiffinsOf(rules[m], food);
    if (!rules[m] || isPlainMeal(tiffins, food)) continue;
    out[m] = {
      mode: 'portions',
      portions: tiffins.map((t) => ({ id: t.id, food: t.food, mode: t.dishes.length ? 'dishes' : 'special', dishes: t.dishes.length ? t.dishes : undefined, quantity: t.quantity })),
    };
  }
  return Object.keys(out).length ? out : undefined;
}
